/**
 * Core: Query
 * -----------------------------------------------------------------------------
 * GraphQL query tool, acts as a replacement for Apollo.
 * - Do not query admin API from JS as you will need to publicly store the API
 *   access token and password which is a security risk.
 *
 */
import { isStorageSupported } from '~/helpers/general'

/**
 * Query storefront API.
 * @param {Object} data - Parameters.
 * @param {Object} data.query - GraphQL query.
 * @param {Object} data.signal - AbortController signal instance.
 * @param {Object} data.variables - GraphQL variables.
 * @param {Boolean|Number} useCache - Use sessionStorage cache, passing a number
 * sets the time limit for the cache in milliseconds.
 * @returns {Promise}
 */
export default (
  { query: graphqlQuery, signal, variables },
  useCache = cnvs.settings.queryCache.duration,
) => {
  return new Promise(async(resolve, reject) => {
    const queryCacheKey = cnvs.settings.queryCache.key
    const { store, token, version } = cnvs.credentials.storefront
    const url = `https://${store}.myshopify.com/api/${version}/graphql.json`

    /**
     * Set headers.
     */
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': token,
    }

    /**
     * Add language header.
     */
    if (variables?.language) {
      const languageCode = getLanguageCode(variables.language)
      headers['Accept-Language'] = languageCode.header
      variables.language = languageCode.variable
    }

    /**
     * Retrieve cached response if sessionStorage is supported and enabled in
     * the config.
     */
    const storageSupported = isStorageSupported()
    let storageKey = ''

    if (storageSupported) {
      storageKey = buildStorageKey(graphqlQuery, variables)

      if (shouldRetrieveCache(useCache)) {
        const queryCache = JSON.parse(sessionStorage.getItem(queryCacheKey))
        const currentTime = new Date().getTime()

        if (queryCache && queryCache[storageKey]) {
          let cacheExpired = false

          /**
           * If useCache is a number then test that cache hasn't expired first
           * before using it.
           * - No need to delete expired cache as it's about to be replaced.
           */
          if (typeof useCache === 'number' && queryCache[storageKey].created) {
            const difference = currentTime - queryCache[storageKey].created
            cacheExpired = difference > useCache
          }

          if (!cacheExpired) {
            resolve(queryCache[storageKey].data)
            return
          }
        }
      }
    }

    /**
     * Fetch response.
     */
    let response = {}

    try {
      response = await fetch(url, {
        body: JSON.stringify({
          query: buildQuery(graphqlQuery),
          variables,
        }),
        headers,
        method: 'POST',
        signal,
      })

      if (!response.ok) {
        reject(response.description)
        return
      }

      response = await response.json()

    } catch (error) {
      reject(error)
      return
    }

    /**
     * Set response to cache if supported and enabled in config.
     */
    if (storageSupported && shouldCacheData(response.data, useCache)) {
      try {
        const currentTime = new Date().getTime()
        const data = JSON.parse(sessionStorage.getItem(queryCacheKey)) || {}

        data[storageKey] = {
          created: currentTime,
          data: response.data,
        }

        sessionStorage.setItem(queryCacheKey, JSON.stringify(data))

      /**
       * An error here can only be because sessionStorage is full due to the
       * isStorageSupported() function.
       * - If storage is full then just resolve response data.
       */
      } catch (error) {
        resolve(response.data)
        return
      }
    }

    resolve(response.data)
  })
}

/**
 * Get language code from language variable.
 * - Storefront API does not support kebab-case language code so this is
 *   converted into SCREAMING_SNAKE_CASE.
 * - E.g. ZH-CN doesn't work, but ZH_CH does.
 * - Does not convert `Accept-Language` header as this expects kebab-case.
 * @param {String} languageVariable - Language variable.
 * @returns {Object}
 */
function getLanguageCode(languageVariable) {
  const sanitisedLanguageVariable = languageVariable.toLowerCase()
  let languageCode = languageVariable

  /**
   * Handle localhost sometimes returning multi-part language code which isn't
   * supported in expected LanguageCode enums.
   * - https://shopify.dev/docs/api/storefront/2023-10/enums/LanguageCode
   * - E.g. Localhost returns ar-AE which isn't a valid language code.
   */
  if (!['pt-br', 'pt-pt', 'zh-cn', 'zh-tw'].includes(sanitisedLanguageVariable)) {
    languageCode = languageCode.split('-')[0]
  }

  return {
    header: languageCode,
    variable: languageCode.toUpperCase().replaceAll('-', '_'),
  }
}

/**
 * Build storage key for sessionStorage caching.
 * @param {Object} astData - AST format GraphQL query.
 * @param {Object} variables - GraphQL variables.
 * @returns {String}
 */
function buildStorageKey(astData, variables = {}) {
  const queryString = astData.definitions
    .map((definition) => {
      const type = definition.operation || 'fragment'
      return `${type}:"${definition.name.value}"`
    })
    .join('-')

  const variableString = Object.entries(variables)
    .map(([key, value]) => {
      return `${key}:${JSON.stringify(value)}`
    })
    .join('-')

  return `${queryString}-length:${astData.loc.end}-${variableString}`
}

/**
 * Returns whether data should be retrieved from cache.
 * @param {Boolean} useCache - Use cache theme setting.
 * @returns {Boolean}
 */
function shouldRetrieveCache(useCache) {
  return (
    useCache &&
    !cnvs.settings.queryCache.disable &&
    !cnvs.storybook
  )
}

/**
 * Returns whether response data should be cached.
 * @param {Object} response - Response data.
 * @param {Boolean} useCache - Use cache theme setting.
 * @returns {Boolean}
 */
function shouldCacheData(response, useCache) {
  if (
    !useCache ||
    cnvs.settings.queryCache.disable ||
    cnvs.storybook ||
    !response
  ) {
    return false
  }

  /**
   * Do not cache if response data has a single falsey property.
   * - Response returns as { [property]: undefined } so just checking object
   *   will always return truthy.
   */
  return !(
    Object.keys(response)?.length === 1 &&
    !response[Object.keys(response)[0]]
  )
}

/**
 * Build GraphQL query string from AST format data.
 * @param {Object} astData - AST format GraphQL query.
 * @returns {String}
 */
function buildQuery(astData) {

  /**
   * Uses body source as starting point.
   * - Remove import tags.
   */
  let query = astData.loc.source.body
    .replace(/#import.*\n/g, '')
    .trim()

  const fragments = {}

  /**
   * Go through each fragment definition and build query string.
   */
  astData.definitions.forEach((definition) => {
    if (definition.kind !== 'FragmentDefinition') {
      return
    }

    const fields = buildFields(definition.selectionSet.selections)
    fragments[definition.name.value] = fields.join('\n')
  })

  /**
   * Replace fragment object with query.
   */
  query = replaceFragments(query, fragments)

  return query
}

/**
 * Build fields from selection sets.
 * - Supports arguments and nested fields.
 * @param {Array} selections - Selections array of objects.
 * @returns {String}
 */
function buildFields(selections) {
  return selections.map((selection) => {
    let field = selection.alias
      ? `${selection.alias?.value}: ${selection.name?.value}`
      : selection.name?.value

    /**
     * Inline fragments.
     */
    if (selection.kind === 'InlineFragment') {
      field = `... on ${selection.typeCondition.name.value}`
    }

    /**
     * Preserves fragment syntax.
     */
    if (selection.kind === 'FragmentSpread') {
      field = `...${selection.name.value}`
    }

    /**
     * If field has argument append to variable.
     */
    if (selection.arguments?.length) {
      const args = selection.arguments.map((argument) => {
        let value = argument.value.value

        switch (argument.value.kind) {
          case 'ListValue':
            value = buildListArgumentValue(argument)
            break

          case 'StringValue':
            value = `"${argument.value.value}"`
            break

          case 'Variable':
            value = `$${argument.value.name.value}`
            break
        }

        return `${argument.name.value}: ${value}`
      })

      field += `(${args.join(', ')})`
    }

    /**
     * If field has nested fields then append to variable.
     */
    if (selection.selectionSet) {
      const fields = buildFields(selection.selectionSet.selections)
      field += ` {\n${fields.join('\n')}\n}`
    }

    return field
  })
}

/**
 * Builds value of argument when it's a list value (array of objects).
 * @param {Object} argument - AST format argument object.
 * @returns {String}
 */
function buildListArgumentValue(argument) {
  const values = argument.value.values.map((value) => {
    const fields = value.fields.map((field) => {
      return `${field.name.value}: "${field.value.value}"`
    })

    return `{${fields.join(', ')}}`
  })

  return `[${values.join(', ')}]`
}

/**
 * Replace fragments.
 * - Goes through each ...key and replaces with corresponding fragment.
 * @returns {String}
 */
function replaceFragments(query, fragments) {
  let localQuery = query

  Object.entries(fragments).forEach(([key, value]) => {
    let formattedValue = value

    /**
     * Resolves nested and nested nested fragments.
     */
    Object.entries(fragments).forEach(([fragmentKey, fragmentValue]) => {
      formattedValue = formattedValue.replaceAll(`...${fragmentKey}`, fragmentValue)
    })

    localQuery = localQuery.replaceAll(`...${key}`, formattedValue)
  })

  return localQuery
}
